import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface PlantRecommendation {
  id: string;
  name: string;
  variety: string;
  quantity: number;
  spacing: string;
  plantingTime: string;
  harvestTime: string;
  expectedYield: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  sunRequirement: string;
  waterRequirement: string;
}

interface RecommendationDetailsProps {
  recommendations: PlantRecommendation[];
  totalArea: number;
}

const RecommendationDetails = ({ recommendations, totalArea }: RecommendationDetailsProps) => {
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-success bg-success/10';
      case 'Medium': return 'text-warning bg-warning/10';
      case 'Hard': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'CheckCircleIcon';
      case 'Medium': return 'ExclamationTriangleIcon';
      case 'Hard': return 'XCircleIcon';
      default: return 'QuestionMarkCircleIcon';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Plant Recommendations</h3>
        <div className="text-sm text-muted-foreground">
          Total Area: {totalArea} sq ft
        </div>
      </div>

      <div className="space-y-4">
        {recommendations.map((plant) => (
          <div key={plant.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors duration-200">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-foreground">{plant.name}</h4>
                <p className="text-sm text-muted-foreground">{plant.variety}</p>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getDifficultyColor(plant.difficulty)}`}>
                <Icon name={getDifficultyIcon(plant.difficulty) as any} size={12} />
                <span>{plant.difficulty}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="flex items-center space-x-1 text-muted-foreground mb-1">
                  <Icon name="HashtagIcon" size={14} />
                  <span>Quantity</span>
                </div>
                <p className="font-medium text-foreground">{plant.quantity} plants</p>
              </div>

              <div>
                <div className="flex items-center space-x-1 text-muted-foreground mb-1">
                  <Icon name="ArrowsRightLeftIcon" size={14} />
                  <span>Spacing</span>
                </div>
                <p className="font-medium text-foreground">{plant.spacing}</p>
              </div>

              <div>
                <div className="flex items-center space-x-1 text-muted-foreground mb-1">
                  <Icon name="SunIcon" size={14} />
                  <span>Sun Need</span>
                </div>
                <p className="font-medium text-foreground">{plant.sunRequirement}</p>
              </div>

              <div>
                <div className="flex items-center space-x-1 text-muted-foreground mb-1">
                  <Icon name="BeakerIcon" size={14} />
                  <span>Water Need</span>
                </div>
                <p className="font-medium text-foreground">{plant.waterRequirement}</p>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-border">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Plant:</span>
                  <span className="ml-2 font-medium text-foreground">{plant.plantingTime}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Harvest:</span>
                  <span className="ml-2 font-medium text-foreground">{plant.harvestTime}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Expected Yield:</span>
                  <span className="ml-2 font-medium text-success">{plant.expectedYield}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecommendationDetails;